import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Database, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";

const LABEL = { beds: "Beds", baths: "Baths", living_sqft: "Living sq ft", lot_size: "Lot size", year_built: "Year built", property_type: "Property type", apn: "APN / parcel", assessed_value: "Assessed value", market_value: "Market value (improvements)", last_sale_date: "Last sale date", last_sale_price: "Last sale price" };
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

export const DealMachinePanel = ({ property, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (!property) return null;
  const dm = property.dealmachine;

  const pull = async (refresh) => {
    if (refresh && !window.confirm("Refresh from DealMachine? This uses DealMachine credits.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/properties/${property.id}/dealmachine`, { refresh });
      toast.success(r.data.cached ? "Showing cached DealMachine data" : "DealMachine data pulled");
      onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "DealMachine pull failed"); } finally { setBusy(false); }
  };
  const verify = async (field) => {
    try { await api.post(`/admin/properties/${property.id}/dealmachine/verify`, { field }); toast.success("Marked verified"); onChange && onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not mark verified"); }
  };

  return (
    <Card className="p-4 border-slate-200" data-testid="dealmachine-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><Database className="w-3.5 h-3.5" /> DealMachine property data</div>
        <div className="flex items-center gap-2">
          {dm && <span className="text-[11px] text-slate-500" data-testid="dm-last-pulled">Last pulled {fmtDate(dm.retrieved_at)}</span>}
          <Button size="sm" variant={dm ? "outline" : "default"} disabled={busy} onClick={() => pull(!!dm)} data-testid="dm-pull-btn" className="min-h-[36px]">
            {busy ? "Pulling…" : (dm ? <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</> : "Pull DealMachine data")}
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">Uses DealMachine credits · public property characteristics only, no owner contact data. Values load as <span className="font-semibold">Estimate</span> evidence and never overwrite an owner-entered or Public Source value.</div>
      {dm && (
        <table className="w-full text-sm mt-3" data-testid="dm-facts-table">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="text-left py-1">Fact</th><th className="text-left py-1">Owner-entered</th><th className="text-left py-1">DealMachine</th><th className="text-right py-1"></th></tr></thead>
          <tbody>
            {Object.entries(dm.facts || {}).map(([k, f]) => (
              <tr key={k} className={`border-t border-slate-100 ${f.mismatch ? "bg-amber-50" : ""}`} data-testid={`dm-fact-${k}`}>
                <td className="py-1.5 font-medium">{LABEL[k] || k}</td>
                <td className="py-1.5 font-mono text-slate-700">{f.owner_value ?? "—"}</td>
                <td className="py-1.5">
                  <span className="font-mono">{String(f.value)}</span>
                  <div className="text-[10px] text-slate-500">Estimate · DealMachine · {fmtDate(f.retrieved_at)}{f.mismatch && <span className="text-amber-700 font-semibold inline-flex items-center gap-0.5 ml-2"><AlertTriangle className="w-3 h-3" /> mismatch</span>}</div>
                </td>
                <td className="py-1.5 text-right">
                  {f.verified_fact ? <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1" data-testid={`dm-verified-${k}`}><ShieldCheck className="w-3 h-3" /> verified {fmtDate(f.verified_at)}</span>
                    : <button onClick={() => verify(k)} className="text-[11px] underline text-slate-700 min-h-[32px]" data-testid={`dm-verify-${k}`}>Mark verified</button>}
                </td>
              </tr>
            ))}
            {Object.keys(dm.facts || {}).length === 0 && <tr><td colSpan={4} className="py-2 text-xs text-slate-500 italic">DealMachine returned no property characteristics for this address.</td></tr>}
          </tbody>
        </table>
      )}
      {dm?.credits && <div className="text-[10px] text-slate-400 mt-2">Credits used on last pull: {JSON.stringify(dm.credits)}</div>}
    </Card>
  );
};
